import { useEffect, useState, type FormEvent } from 'react';
import { Check, Clock3, Gavel, LockKeyhole, Plus, ShieldAlert, UserCheck, XCircle } from 'lucide-react';
import { api, ApiError } from '../api';
import { useAuth } from '../context/AuthContext';
import { useUi } from '../context/UiContext';
import type { Auction, AuctionAccess, AuctionApplication } from '../types';
import { currencyInputToCents, formatMoney, formatTime, relativeTime } from '../utils';
import { ActionSheet, Badge, Button, CollapsiblePanel, EmptyBlock, Fab, Field, LoadingBlock, PageHeader, Panel, Textarea, TextInput } from '../components/ui';

const auctionTone = { active: 'active', sold: 'good', unsold: 'muted', cancelled: 'danger' } as const;
const auctionLabel = { active: '进行中', sold: '已成交', unsold: '流拍', cancelled: '已取消' } as const;
const accessLabel: Record<AuctionAccess['status'], string> = {
  none: '未申请',
  pending: '审核中',
  approved: '已开通',
  rejected: '未通过'
};

export function AuctionsPage({ onNeedAuth }: { onNeedAuth: () => void }) {
  const { user, settings, permissions, refresh } = useAuth();
  const { toast, confirm } = useUi();
  const [items, setItems] = useState<Auction[] | null>(null);
  const [access, setAccess] = useState<AuctionAccess | null>(null);
  const [applications, setApplications] = useState<AuctionApplication[]>([]);
  const [formOpen, setFormOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [accessBusy, setAccessBusy] = useState(false);
  const [bidInputs, setBidInputs] = useState<Record<string, string>>({});
  const [, setTick] = useState(0);

  const load = async () => {
    const accessRequest = user
      ? api<{ access: AuctionAccess }>('/api/auctions/access')
      : Promise.resolve({ access: null });
    const applicationRequest = permissions.isStaff
      ? api<{ applications: AuctionApplication[] }>('/api/admin/auction-applications?status=pending')
      : Promise.resolve({ applications: [] });
    const [auctionData, accessData, applicationData] = await Promise.all([
      api<{ auctions: Auction[]; tradeFeePercent: number }>('/api/auctions'),
      accessRequest,
      applicationRequest
    ]);
    setItems(auctionData.auctions);
    setAccess(accessData.access);
    setApplications(applicationData.applications);
  };

  useEffect(() => {
    void load().catch(() => toast('拍卖数据加载失败', 'error'));
    const timer = window.setInterval(() => { setTick(value => value + 1); void load().catch(() => {}); }, 30_000);
    return () => window.clearInterval(timer);
  }, [user?.id, permissions.isStaff]);

  useEffect(() => {
    const timer = window.setInterval(() => setTick(value => value + 1), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const canUseAuction = Boolean(access?.canUse);

  const needBinding = () => {
    if (!user) { onNeedAuth(); return true; }
    if (!user.boundToGame) { toast('请先绑定游戏角色后再使用拍卖功能', 'error'); return true; }
    return false;
  };

  const needAuctionAccess = () => {
    if (needBinding()) return true;
    if (!canUseAuction) {
      toast('请先提交拍卖权限申请，等待管理员审核通过', 'error');
      return true;
    }
    return false;
  };

  const openForm = () => {
    if (needAuctionAccess()) return;
    setFormOpen(true);
  };

  const applyAccess = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (needBinding()) return;
    const formEl = event.currentTarget;
    const reason = String(new FormData(formEl).get('reason') || '').trim();
    setAccessBusy(true);
    try {
      const data = await api<{ access: AuctionAccess }>('/api/auctions/access/apply', { method: 'POST', body: JSON.stringify({ reason }) });
      setAccess(data.access);
      formEl.reset();
      toast('拍卖权限申请已提交', 'success');
    } catch (error) {
      toast(error instanceof ApiError ? error.message : '申请失败', 'error');
    } finally {
      setAccessBusy(false);
    }
  };

  const reviewApplication = async (application: AuctionApplication, status: 'approved' | 'rejected') => {
    const approved = await confirm({
      title: status === 'approved' ? '通过拍卖申请' : '驳回拍卖申请',
      body: `确认${status === 'approved' ? '开通' : '驳回'}「${application.displayName}」的拍卖权限？`,
      danger: status === 'rejected',
      confirmText: status === 'approved' ? '通过申请' : '确认驳回'
    });
    if (!approved) return;
    try {
      await api('/api/admin/auction-applications/' + application.userId, {
        method: 'PATCH',
        body: JSON.stringify({ status, reviewNote: status === 'approved' ? '管理员审核通过' : '管理员审核驳回' })
      });
      await load();
      toast('申请已处理', 'success');
    } catch (error) {
      toast(error instanceof ApiError ? error.message : '处理失败', 'error');
    }
  };

  const create = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (needAuctionAccess()) return;
    const formEl = event.currentTarget;
    const form = new FormData(formEl);
    const end = String(form.get('endAt') || '');
    const payload = { name: String(form.get('name') || ''), description: String(form.get('description') || ''), startPriceCents: currencyInputToCents(String(form.get('startPrice') || '0')), stepCents: currencyInputToCents(String(form.get('step') || '0')), endAt: new Date(end).getTime() };
    setBusy(true);
    try {
      await api('/api/auctions', { method: 'POST', body: JSON.stringify(payload) });
      formEl.reset();
      setFormOpen(false);
      await load();
      toast('拍卖已发起', 'success');
    } catch (error) {
      toast(error instanceof ApiError ? error.message : '发起失败', 'error');
    } finally {
      setBusy(false);
    }
  };

  const bid = async (item: Auction, event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (needAuctionAccess()) return;
    const amountCents = currencyInputToCents(bidInputs[item.id] || '0');
    try {
      await api('/api/auctions/' + item.id + '/bids', { method: 'POST', body: JSON.stringify({ amountCents }) });
      await Promise.all([load(), refresh()]);
      setBidInputs(current => ({ ...current, [item.id]: '' }));
      toast('出价成功', 'success');
    } catch (error) {
      toast(error instanceof ApiError ? error.message : '出价失败', 'error');
    }
  };

  const quickAdd = (item: Auction, minimum: number, amount: number) => {
    const current = Number(bidInputs[item.id] || minimum / 100) || minimum / 100;
    setBidInputs(inputs => ({ ...inputs, [item.id]: String(current + amount) }));
  };

  const cancel = async (item: Auction, staff = false) => {
    if (needBinding()) return;
    const approved = await confirm({ title: staff ? '管理员撤销拍卖' : '确认要撤拍？', body: staff ? '当前领先者的冻结资金会原路退回，操作将写入审计日志。' : '拍卖将立即结束，当前领先者资金会退回。', danger: true, confirmText: '确认撤销' });
    if (!approved) return;
    try {
      await api('/api/auctions/' + item.id + '/cancel', { method: 'POST' });
      await Promise.all([load(), refresh()]);
      toast('拍卖已撤销', 'success');
    } catch (error) {
      toast(error instanceof ApiError ? error.message : '撤销失败', 'error');
    }
  };

  const accessHint = !user
    ? '登录并绑定游戏角色后，可以提交拍卖权限申请。'
    : !user.boundToGame
      ? '请先在“我的账户”绑定游戏角色。'
      : canUseAuction
        ? (access?.staffBypass ? '管理员账号可直接使用拍卖功能。' : '拍卖权限已开通，可以发起拍卖和参与出价。')
        : access?.status === 'pending'
          ? '申请已提交，等待管理员审核。'
          : access?.status === 'rejected'
            ? '申请未通过，可以修改理由后重新提交。'
            : '提交申请后，管理员审核通过才能发起拍卖或参与出价。';

  return (
    <>
      <PageHeader title="法拍场" description={`出价最高者成交；领先出价资金会被冻结，被超过后自动退回。物品成交收取 ${settings.tradeFeePercent}% 手续费。`} />
      <Panel title="拍卖权限" eyebrow="AUCTION ACCESS">
        <div className="auction-access-row">
          <div><p>{accessHint}</p>{access?.reviewNote && <small className="muted">审核备注：{access.reviewNote}</small>}</div>
          <Badge tone={canUseAuction ? 'good' : access?.status === 'pending' ? 'warn' : access?.status === 'rejected' ? 'danger' : 'muted'}>{access ? accessLabel[access.status] : user ? '读取中' : '未登录'}</Badge>
        </div>
        {user && user.boundToGame && !canUseAuction && access?.status !== 'pending' && (
          <form className="stack-form top-gap" onSubmit={applyAccess}>
            <Field label="申请理由" hint="说明你想使用拍卖行做什么，方便管理员判断"><Textarea name="reason" rows={3} maxLength={300} required placeholder="例如：我想拍卖一组稀有材料和装备。" /></Field>
            <Button variant="primary" type="submit" loading={accessBusy}><UserCheck size={16} /> 提交拍卖申请</Button>
          </form>
        )}
        {!user && <Button variant="primary" className="top-gap" onClick={onNeedAuth}>登录 / 注册</Button>}
      </Panel>

      {permissions.isStaff && (
        <Panel title="待审核拍卖申请" eyebrow="AUCTION APPLICATIONS" className="top-gap">
          {applications.length === 0 ? <EmptyBlock title="暂无待审核申请" /> : (
            <div className="compact-list">
              {applications.map(application => (
                <div className="compact-row" key={application.userId}>
                  <div><strong>{application.displayName} <small>@{application.username}</small></strong><small>{application.reason} · {formatTime(application.appliedAt)}</small></div>
                  <div className="inline-actions"><Button variant="primary" onClick={() => void reviewApplication(application, 'approved')}><Check size={15} />通过</Button><Button variant="danger" onClick={() => void reviewApplication(application, 'rejected')}>驳回</Button></div>
                </div>
              ))}
            </div>
          )}
        </Panel>
      )}

      <ActionSheet open={formOpen} onClose={() => setFormOpen(false)} title="发起拍卖" eyebrow="NEW AUCTION" description={`设置起拍价、加价幅度和结束时间。成交后扣除 ${settings.tradeFeePercent}% 手续费。`}>
        <form className="stack-form" onSubmit={create}>
          <Field label="拍品名称"><TextInput name="name" maxLength={60} required placeholder="例：下界合金剑" /></Field>
          <Field label="说明"><Textarea name="description" rows={4} maxLength={600} placeholder="拍品属性、数量、交付方式" /></Field>
          <div className="form-grid"><Field label={'起拍价（' + settings.currency + '）'}><TextInput name="startPrice" type="number" min="0.01" step="0.01" required /></Field><Field label={'最低加价（' + settings.currency + '）'}><TextInput name="step" type="number" min="0.01" step="0.01" defaultValue="1" required /></Field></div>
          <Field label="结束时间"><TextInput name="endAt" type="datetime-local" required /></Field>
          <Button variant="primary" type="submit" loading={busy} className="wide"><Plus size={17} /> 发起拍卖</Button>
        </form>
      </ActionSheet>

      <CollapsiblePanel title={'拍卖列表' + (items ? ' · ' + items.length : '')} eyebrow="AUCTION BOARD" className="top-gap" defaultOpen={false}>
        {!items ? <LoadingBlock /> : items.length === 0 ? <EmptyBlock title="暂无拍卖" description={canUseAuction ? '发起第一场拍卖，让成员开始竞价。' : '申请通过后可以发起第一场拍卖。'} action={canUseAuction ? <Button variant="primary" onClick={openForm}><Gavel size={16} /> 发起第一场拍卖</Button> : undefined} /> : (
          <div className="card-list">
            {items.map(item => {
              const active = item.status === 'active';
              const minimum = item.topBidderId ? item.topPriceCents + item.stepCents : item.startPriceCents;
              const mine = user?.id === item.sellerId;
              return (
                <article className="record-card auction-card" key={item.id}>
                  {active && mine && <button className="corner-action danger-text" onClick={() => void cancel(item)}><XCircle size={14} /> 撤拍</button>}
                  <header><div><span className="record-type">AUCTION</span><h3>{item.name}</h3><small>{item.sellerName} · {formatTime(item.createdAt)}</small></div><Badge tone={auctionTone[item.status]}>{auctionLabel[item.status]}</Badge></header>
                  {item.description && <p className="record-description">{item.description}</p>}
                  <div className="auction-metrics"><div><span>当前价格</span><strong>{formatMoney(item.topBidderId ? item.topPriceCents : item.startPriceCents, settings.currency)}</strong></div><div><span>领先者</span><strong>{item.topBidderName || '暂无'}</strong></div><div><span>加价幅度</span><strong>{formatMoney(item.stepCents, settings.currency)}</strong></div><div><span>{active ? '剩余时间' : '结束时间'}</span><strong><Clock3 size={14} /> {active ? relativeTime(item.endAt) : formatTime(item.endAt)}</strong></div></div>
                  {item.status === 'sold' && item.feeCents !== null && <p className="form-note">成交价 {formatMoney(item.topPriceCents, settings.currency)} · 手续费 {formatMoney(item.feeCents || 0, settings.currency)} · 卖家实收 {formatMoney(item.sellerNetCents || 0, settings.currency)}</p>}
                  {active && !mine && canUseAuction && <form className="bid-form" onSubmit={event => void bid(item, event)}><TextInput name="amount" type="number" min={minimum / 100} step="0.01" value={bidInputs[item.id] || ''} onChange={event => setBidInputs(inputs => ({ ...inputs, [item.id]: event.target.value }))} placeholder={'最低 ' + formatMoney(minimum, settings.currency)} required /><Button variant="primary" type="submit"><Gavel size={16} /> 出价</Button></form>}
                  {active && !mine && canUseAuction && <div className="quick-bids"><span>快捷加价</span>{[10, 50, 100].map(amount => <button type="button" key={amount} onClick={() => quickAdd(item, minimum, amount)}>+{amount}</button>)}</div>}
                  {active && !mine && !canUseAuction && <p className="form-note"><LockKeyhole size={14} /> 拍卖权限审核通过后才能出价。</p>}
                  {active && mine && <p className="form-note">这是你发起的拍卖，不能参与出价。</p>}
                  {item.bids.length > 0 && <details className="bid-history"><summary>出价记录（{item.bids.length}）</summary>{item.bids.slice(0, 8).map(log => <div key={log.id}><span>{log.bidderName}</span><b>{formatMoney(log.amountCents, settings.currency)}</b><small>{formatTime(log.createdAt)}</small></div>)}</details>}
                  {active && permissions.isStaff && !mine && <footer className="record-actions"><Button variant="danger" onClick={() => void cancel(item, true)}><ShieldAlert size={16} /> 管理撤销</Button></footer>}
                </article>
              );
            })}
          </div>
        )}
      </CollapsiblePanel>
      {canUseAuction && <Fab label="发起拍卖" onClick={openForm} />}
    </>
  );
}